// Copyright (c) Microgreen Manager LLC. All rights reserved. Proprietary and confidential. Unauthorized copying is strictly prohibited.
import { useApolloClient, useMutation, useQuery } from '@apollo/client';
import { ORDER_STATUS, PLANTING_STATUS, utils } from '@seedplanner/shared-utils';
import gql from 'graphql-tag';
import { enqueueSnackbar } from 'notistack';
import * as React from 'react';
import { useNavigate } from 'react-router';
import { useGating, useUserContext } from '../../context/UserContext';
import { MenuItem } from '../../lib/base';
import { AddIcon, CancelIcon, DeleteIcon, EditIcon, FileCopyIcon } from '../../lib/icons';
import { CUSTOMERS_QUERY } from '../../queries/CustomersQuery';
import { SURPLUSES_BUFFERS_QUERY } from '../../queries/SurplusesQueries';
import * as routes from '../../routes';
import type {
  CancelOrderDataQuery,
  CancelOrderDataQueryVariables,
  CancelOrderMutation,
  CancelOrderMutationVariables,
  DeleteOrderMutation,
  DeleteOrderMutationVariables,
} from '../../types/__generated__/graphql';
import { createID, getTimeRanges } from '../../utils';
import { evictCacheData } from '../../utils/evictCacheData';
import { Confirmation } from '../common/dailog';
import { Options } from '../common/dropdown';
import { PageError } from '../common/error';
import { PageLoading } from '../common/loading';
import AddPlantingDialog from '../planting/AddPlantingDialog';
import AddOrderDialog from './AddOrderDialog';
import EditOrderCustomerDialog from './EditOrderCustomerDialog';
import EditOrderInputsDialog from './EditOrderInputsDialog';
import EditOrderNotesDialog from './EditOrderNotesDialog';
import EditOrderTimeDialog from './EditOrderTimeDialog';
import { PlantingListContainer } from './PlantingListContainer';

const CANCEL_ORDER_MUT = gql`
  mutation CancelOrder($id: Int!) {
    cancelOrder(input: { order_id: $id }) {
      updated_order {
        id
        order_status
        updated_at
      }
      deleted_tasks {
        id
      }
      deleted_plantings {
        id
      }
      updated_tasks {
        id
        order_id
        updated_at
      }
      updated_plantings {
        id
        order_id
        planting_state
        updated_at
      }
    }
  }
`;

const CANCEL_ORDER_DATA = gql`
  query CancelOrderData($id: ID!) {
    order(id: $id) {
      id
      plantings {
        edges {
          node {
            id
            planting_status
            trays_rounded
            yield_per_tray_ounces
            crop {
              id
              crop_name
              yield_unit
            }
          }
        }
      }
    }
  }
`;

const DELETE_ORDER_MUT = gql`
  mutation DeleteOrder($id: Int!) {
    deleteSingleOrder(input: { order_id: $id }) {
      deleted_order {
        id
      }
      deleted_order_inputs {
        id
      }
      deleted_plantings {
        id
      }
      deleted_tasks {
        id
      }
      updated_recurring_order {
        id
        recurring_options {
          exceptions
        }
        updated_at
      }
    }
  }
`;

function CancelOrderInfo({ orderId }: { orderId: number }) {
  const { data, loading, error } = useQuery<CancelOrderDataQuery, CancelOrderDataQueryVariables>(CANCEL_ORDER_DATA, {
    variables: { id: createID.order(orderId) },
    defaultOptions: {
      fetchPolicy: 'network-only',
    },
  });
  if (loading) {
    return <PageLoading />;
  }
  if (error) {
    return <PageError error={error} />;
  }
  const plantings = (data?.order?.plantings?.edges ?? []).map((edge) => edge.node);
  const surplusPlantings = plantings.filter((p) => p.planting_status !== PLANTING_STATUS.PLANNED);
  const cancelledPlantings = plantings.filter((p) => p.planting_status === PLANTING_STATUS.PLANNED);

  return (
    <>
      {cancelledPlantings.length > 0 && (
        <PlantingListContainer
          plantingsIn={cancelledPlantings}
          title="These planned plantings will be deleted:"
          showAmount={false}
        />
      )}
      {surplusPlantings.length > 0 && (
        <PlantingListContainer
          plantingsIn={surplusPlantings}
          title="These in progress and completed plantings will be added to your surpluses:"
          showAmount={true}
        />
      )}
    </>
  );
}

interface Props {
  readonly orderId: number;
  readonly orderFulfillmentTime: number;
  readonly onSuccess: (shouldRefetchSelf: boolean) => void;
  readonly useMoreIcon?: boolean;
  readonly orderStatus: string;
}

export default function OrderOptions({ orderId, orderFulfillmentTime, onSuccess, useMoreIcon, orderStatus }: Props) {
  const navigate = useNavigate();
  const { timezone } = useUserContext();
  const { canUseBasic } = useGating();
  const { todayStart } = getTimeRanges(timezone);
  const [newOrderOpen, setNewOrderOpen] = React.useState(false);
  const [editInputsOpen, setEditInputsOpen] = React.useState(false);
  const [editOrderCustomerOpen, setEditOrderCustomerOpen] = React.useState(false);
  const [editTimeOpen, setEditTimeOpen] = React.useState(false);
  const [optionsClose, setOptionsClose] = React.useState(false);
  const [editOrderNotesOpen, setEditOrderNotesOpen] = React.useState(false);
  const [cancelConfirmationOpen, setCancelConfirmationOpen] = React.useState(false);
  const [deleteConfirmationOpen, setDeleteConfirmationOpen] = React.useState(false);
  const [addPlantingOpen, setAddPlantingOpen] = React.useState(false);
  const apolloClient = useApolloClient();
  const [cancelOrder, { loading: cancelOrderLoading }] = useMutation<CancelOrderMutation, CancelOrderMutationVariables>(
    CANCEL_ORDER_MUT,
    {
      variables: { id: orderId },
    },
  );
  const [deleteOrder, { loading: deleteOrderLoading }] = useMutation<DeleteOrderMutation, DeleteOrderMutationVariables>(
    DELETE_ORDER_MUT,
    {
      variables: { id: orderId },
    },
  );

  React.useEffect(() => {
    setOptionsClose(false);
  });

  const editOrderInputsItem = (
    <MenuItem
      key="edit-order-inputs"
      onClick={() => {
        setEditInputsOpen(true);
      }}
      disableRipple
      disabled={!canUseBasic}
    >
      <EditIcon />
      Edit Contents
    </MenuItem>
  );
  const editOrderTime = (
    <MenuItem
      key="edit-order-time"
      onClick={() => {
        setEditTimeOpen(true);
      }}
      disableRipple
      disabled={!canUseBasic}
    >
      <EditIcon />
      Edit Delivery Date
    </MenuItem>
  );
  const editOrderNotesItem = (
    <MenuItem
      key="edit-order-notes"
      onClick={() => {
        setEditOrderNotesOpen(true);
      }}
      disableRipple
      disabled={!canUseBasic}
    >
      <EditIcon />
      Edit Notes
    </MenuItem>
  );
  const editOrderCustomerItem = (
    <MenuItem
      key="edit-order-customer"
      onClick={() => {
        setEditOrderCustomerOpen(true);
      }}
      disableRipple
      disabled={!canUseBasic}
    >
      <EditIcon />
      Edit Customer
    </MenuItem>
  );
  const clone = (
    <MenuItem
      key="clone-order"
      onClick={() => {
        setNewOrderOpen(true);
      }}
      disableRipple
      disabled={!canUseBasic}
    >
      <FileCopyIcon />
      Clone
    </MenuItem>
  );

  const cancelConfirmation = (
    <Confirmation
      open={cancelConfirmationOpen}
      disableSubmit={cancelOrderLoading}
      onSubmit={() => {
        void cancelOrder({
          refetchQueries: [{ query: SURPLUSES_BUFFERS_QUERY, variables: { timeAfter: todayStart } }],
          onCompleted: (cancelDataIn) => {
            const cancelData = cancelDataIn.cancelOrder;
            const deletedPlantingIds = cancelData?.deleted_plantings ?? [];
            const deletedTaskIds = cancelData?.deleted_tasks ?? [];
            evictCacheData({
              apolloClient,
              plantingIds: deletedPlantingIds.map((p) => p.id),
              taskIds: deletedTaskIds.map((t) => t.id),
            });
            enqueueSnackbar('Cancelled order', { variant: 'success' });
            setCancelConfirmationOpen(false);
            setOptionsClose(true);
            onSuccess(true);
          },
          update: (cache, { data }) => {
            const plantingsDeleted = (data?.cancelOrder?.deleted_plantings?.length ?? 0) > 0;
            const tasksDeleted = (data?.cancelOrder?.deleted_tasks?.length ?? 0) > 0;
            cache.modify({
              fields: {
                plantingGroup(existingGroup, { DELETE }) {
                  return plantingsDeleted ? DELETE : existingGroup;
                },
                planting_groups(existingGroup, { DELETE }) {
                  return plantingsDeleted ? DELETE : existingGroup;
                },
                taskGroup(existingGroup, { DELETE }) {
                  return tasksDeleted ? DELETE : existingGroup;
                },
              },
            });
          },
          onError: (errorIn) => {
            enqueueSnackbar(errorIn.message, { variant: 'error', autoHideDuration: 10000 });
          },
        });
      }}
      onClose={() => {
        setCancelConfirmationOpen(false);
        setOptionsClose(true);
      }}
      title="Cancel Order"
      description="Are you sure you want to cancel this order?"
      extra={<CancelOrderInfo orderId={orderId} />}
    />
  );

  const deleteConfirmation = (
    <Confirmation
      open={deleteConfirmationOpen}
      disableSubmit={deleteOrderLoading}
      onSubmit={() => {
        void deleteOrder({
          refetchQueries: [{ query: CUSTOMERS_QUERY }],
          variables: { id: orderId },
          onCompleted: (dataIn) => {
            const data = dataIn.deleteSingleOrder;
            if (data?.deleted_order != null) {
              const deletedOrderId = data.deleted_order.id;
              const deletedPlantingIds = data.deleted_plantings.map((p) => p.id);
              const deletedTaskIds = data.deleted_tasks.map((t) => t.id);
              const deletedOrderInputIds = data.deleted_order_inputs.map((i) => i.id);
              evictCacheData({
                apolloClient,
                orderIds: [deletedOrderId],
                plantingIds: deletedPlantingIds,
                taskIds: deletedTaskIds,
                orderInputIds: deletedOrderInputIds,
              });

              enqueueSnackbar('Deleted order', { variant: 'success' });
              setDeleteConfirmationOpen(false);
              setOptionsClose(true);
              onSuccess(false);
              navigate(routes.ORDERS);
            }
          },
          onError: (errorIn) => {
            enqueueSnackbar(errorIn.message, { variant: 'error', autoHideDuration: 10000 });
          },
        });
      }}
      onClose={() => {
        setDeleteConfirmationOpen(false);
        setOptionsClose(true);
      }}
      title="Delete Order"
      description="Are you sure you want to permanently delete this order? Deleting the order will permanently delete all plantings and tasks associated with this order. This cannot be undone."
    />
  );

  const cancel = (
    <MenuItem
      key="cancel-order"
      onClick={() => {
        setCancelConfirmationOpen(true);
      }}
      disableRipple
      disabled={!canUseBasic}
    >
      <CancelIcon />
      Cancel Order
    </MenuItem>
  );
  const isNotCompletedOrCancelled = orderStatus !== ORDER_STATUS.COMPLETED && orderStatus !== ORDER_STATUS.CANCELLED;
  const deleteOrderMenuItem = (
    <MenuItem
      key="delete-order"
      onClick={() => {
        setDeleteConfirmationOpen(true);
      }}
      disableRipple
      disabled={!canUseBasic}
    >
      <DeleteIcon />
      Delete Order
    </MenuItem>
  );
  const canAddPlanting = orderStatus === ORDER_STATUS.IN_PROGRESS;
  const addPlantingItem = (
    <MenuItem
      key="add-planting"
      onClick={() => {
        setAddPlantingOpen(true);
      }}
      disableRipple
      disabled={!canUseBasic}
    >
      <AddIcon />
      Add New Planting
    </MenuItem>
  );
  const canBeDeleted = orderStatus === ORDER_STATUS.CANCELLED || orderStatus === ORDER_STATUS.PLANNED;
  const options = [
    isNotCompletedOrCancelled ? cancel : null,
    isNotCompletedOrCancelled ? editOrderInputsItem : null,
    isNotCompletedOrCancelled ? editOrderTime : null,
    editOrderCustomerItem,
    editOrderNotesItem,
    canAddPlanting ? addPlantingItem : null,
    canBeDeleted ? deleteOrderMenuItem : null,
    clone,
  ].filter(utils.notNull);

  return (
    <>
      <Options useMoreIcon={useMoreIcon} close={optionsClose} options={options} />
      {cancelConfirmationOpen && cancelConfirmation}
      {newOrderOpen && (
        <AddOrderDialog
          isOpen={newOrderOpen}
          onClose={() => {
            setNewOrderOpen(false);
            setOptionsClose(true);
          }}
          onSuccess={() => {
            setNewOrderOpen(false);
            setOptionsClose(true);
            onSuccess(false);
          }}
          cloneOrderInfo={{
            orderId: orderId,
            orderTime: orderFulfillmentTime,
          }}
          isForNewSingleOrder={true}
        />
      )}
      {editTimeOpen && (
        <EditOrderTimeDialog
          isOpen={editTimeOpen}
          onClose={() => {
            setEditTimeOpen(false);
            setOptionsClose(true);
          }}
          onSuccess={(shouldRefetchSelf) => {
            setEditTimeOpen(false);
            setOptionsClose(true);
            onSuccess(shouldRefetchSelf);
          }}
          orderId={orderId}
        />
      )}
      {editOrderNotesOpen && (
        <EditOrderNotesDialog
          orderId={orderId}
          isOpen={editOrderNotesOpen}
          onClose={() => {
            setEditOrderNotesOpen(false);
            setOptionsClose(true);
          }}
          onSuccess={(shouldRefetchSelf) => {
            setEditOrderNotesOpen(false);
            setOptionsClose(true);
            onSuccess(shouldRefetchSelf);
          }}
        />
      )}
      {editOrderCustomerOpen && (
        <EditOrderCustomerDialog
          orderId={orderId}
          isOpen={editOrderCustomerOpen}
          onClose={() => {
            setEditOrderCustomerOpen(false);
            setOptionsClose(true);
          }}
          onSuccess={(shouldRefetchSelf) => {
            setEditOrderCustomerOpen(false);
            setOptionsClose(true);
            onSuccess(shouldRefetchSelf);
          }}
        />
      )}
      {editInputsOpen && (
        <EditOrderInputsDialog
          orderId={orderId}
          orderTime={orderFulfillmentTime}
          isOpen={editInputsOpen}
          onClose={() => {
            setEditInputsOpen(false);
            setOptionsClose(true);
          }}
          onSuccess={(shouldRefetchSelf) => {
            setEditInputsOpen(false);
            setOptionsClose(true);
            onSuccess(shouldRefetchSelf);
          }}
        />
      )}
      {addPlantingOpen && (
        <AddPlantingDialog
          isOpen={addPlantingOpen}
          onClose={() => {
            setAddPlantingOpen(false);
            setOptionsClose(true);
          }}
          onSuccess={(shouldRefetchSelf) => {
            setAddPlantingOpen(false);
            setOptionsClose(true);
            onSuccess(shouldRefetchSelf ?? false);
          }}
          orderInfo={{
            orderId: orderId,
            orderFulfillmentTime: orderFulfillmentTime,
          }}
        />
      )}
      {deleteConfirmationOpen && deleteConfirmation}
    </>
  );
}
